// src/components/CampaignTutorial.jsx
import React, { useState, useEffect } from "react";

function CampaignTutorial({ onClose }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [highlights, setHighlights] = useState([]);
    const [heroVisible, setHeroVisible] = useState(false); // big connect hero shown?
    const [modalOpen, setModalOpen] = useState(false);     // NewCampaignModal open?

    const handleClose = (completed = false) => {
        if (completed) {
            localStorage.setItem("campaignTutorialCompleted", "true");
        }
        onClose();
    };

    const steps = [
        {
            title: "Welcome to Campaigns",
            description:
                "This is where you manage your automated email campaigns. You’ll connect a mailbox, create campaigns, and track performance.",
            position: "center",
            isIntro: true,
        },
        {
            title: "Link your mailbox",
            description:
                "If you see this card, use it to connect the email account you’ll send from. Or click “Skip for now” if you want to do it later.",
            selector: ".psl-camp-banner .psl-btn.primary, .psl-connect-card",
            position: "bottom",
            autoSkipIfMissing: true,        // skip step if hero/banner isn’t present
            lockNextWhileHeroVisible: true, // block Next while big hero is visible
        },
        {
            title: "Search campaigns",
            description:
                "Use this search box to quickly find a campaign by name or ID. You can skip this for now if you don’t have any campaigns yet.",
            selector: ".psl-toolbar .psl-actions-row--search .psl-input--search",
            position: "bottom",
        },
        {
            title: "Create a new campaign",
            description:
                "Click “New Campaign” to open the creation dialog.",
            selector: ".psl-toolbar .psl-actions-row .psl-btn.primary",
            position: "bottom",
            requireModalOpenForProgress: true, // ❗ must open the modal before Next/Skip
        },
        {
            title: "Name your campaign",
            description:
                "In this dialog, name your campaign after the genre(s) of the music you want to promote. For example: “EDM Playlists” or “House / Techno”. Then click “Create” to jump into the steps editor.",
            selectors: [
                ".psl-modal-card .psl-input--campaign-name",
                ".psl-modal-card .psl-btn--create-campaign",
            ],
            position: "bottom",
            autoSkipIfMissing: true,
            noHighlight: true,
        },
        {
            title: "Campaign stats at a glance",
            description:
                "Here you can see how many contacts are active, paused, finished, bounced, and how many replied.",
            selector: ".psl-tableblock",
            position: "top",
        },
        {
            title: "Clone or edit a campaign",
            description:
                "Use this ⋯ menu to reopen a campaign in the steps editor or clone a successful one for a new genre.",
            selector: ".psl-kebab",
            position: "left",
        },
        {
            title: "Next: build your sequence",
            description:
                "After creating a campaign, you’ll be taken to the steps editor page. There, another guide will help you add emails and timing.",
            selector: ".psl-toolbar .psl-actions-row .psl-btn.primary",
            position: "top",
        },
    ];

    useEffect(() => {
        const updateHighlights = () => {
            const step = steps[currentStep];

            // Track connect hero visibility only on the mailbox step
            if (step.lockNextWhileHeroVisible) {
                const hero = document.querySelector(".psl-connect-card");
                setHeroVisible(!!hero);
            } else {
                setHeroVisible(false);
            }

            // Track NewCampaignModal visibility only on the new-campaign step
            if (step.requireModalOpenForProgress) {
                const modal = document.querySelector(".psl-modal-root");
                setModalOpen(!!modal);
            } else {
                setModalOpen(false);
            }

            if (step.selectors) {
                const found = [];

                step.selectors.forEach(sel => {
                    const el = document.querySelector(sel);
                    if (!el) return;

                    const rect = el.getBoundingClientRect();
                    found.push({
                        ...step,
                        rect: {
                            top: rect.top + window.scrollY,
                            left: rect.left + window.scrollX,
                            width: rect.width,
                            height: rect.height,
                        }
                    });
                });

                if (found.length > 0) {
                    setHighlights(found);
                } else if (step.autoSkipIfMissing) {
                    setCurrentStep(prev => (prev >= steps.length - 1 ? prev : prev + 1));
                } else {
                    setHighlights([]);
                }
            }
            else if (step.selector) {
                // keep backward compatibility for single selector logic
                const element = document.querySelector(step.selector);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    setHighlights([
                        {
                            ...step,
                            rect: {
                                top: rect.top + window.scrollY,
                                left: rect.left + window.scrollX,
                                width: rect.width,
                                height: rect.height,
                            }
                        }
                    ]);
                } else {
                    if (step.autoSkipIfMissing) {
                        setCurrentStep(prev => (prev >= steps.length - 1 ? prev : prev + 1));
                    } else {
                        setHighlights([]);
                    }
                }
            }
            else {
                // Intro step: center the tooltip
                setHighlights([
                    {
                        ...step,
                        rect: {
                            top: window.innerHeight / 2,
                            left: window.innerWidth / 2,
                            width: 0,
                            height: 0,
                        },
                    },
                ]);
            }
        };

        updateHighlights();
        window.addEventListener("resize", updateHighlights);
        window.addEventListener("scroll", updateHighlights);
        return () => {
            window.removeEventListener("resize", updateHighlights);
            window.removeEventListener("scroll", updateHighlights);
        };
    }, [currentStep, steps]);

    const nextStep = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep((s) => s + 1);
        } else {
            handleClose(true);
        }
    };

    const skipTutorial = () => {
        handleClose(true);
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep((s) => s - 1);
        }
    };

    const step = steps[currentStep];
    const highlight = highlights[0];

    // Lock “Next” while:
    // - connect hero is visible on mailbox step, OR
    // - NewCampaignModal is NOT open on the “Create a new campaign” step
    const lockNext =
        (step.lockNextWhileHeroVisible && heroVisible) ||
        (step.requireModalOpenForProgress && !modalOpen);

    // Lock “Skip” ONLY for the “Create a new campaign” step until modal opens
    const lockSkip =
        step.requireModalOpenForProgress && !modalOpen;

    return (
        <div>
            {/* Blurred backdrop for intro step */}
            {step.isIntro && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(17, 19, 24, 0.6)",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(4px)",
                        zIndex: 900,
                        pointerEvents: "none",
                    }}
                />
            )}

            {/* Current step highlight */}
            {!step.noHighlight &&
                highlights.map((h, i) => (
                    <div
                        key={i}
                        className="psl-tutorial-highlight active"
                        style={{
                            position: "absolute",
                            top: h.rect.top - 4,
                            left: h.rect.left - 4,
                            width: h.rect.width + 8,
                            height: h.rect.height + 8,
                            zIndex: 1199,
                            pointerEvents: "none",
                        }}
                    />
                ))}

            {/* Tutorial tooltip */}
            {highlight && (
                <div
                    className="psl-tutorial-tooltip"
                    style={{
                        position: "absolute",
                        top:
                            step.position === "center"
                                ? "50%"
                                : step.position === "top"
                                    ? highlight.rect.top - 130
                                    : highlight.rect.top + highlight.rect.height + 10,
                        left:
                            step.position === "center"
                                ? "50%"
                                : Math.max(
                                    10,
                                    Math.min(
                                        highlight.rect.left + highlight.rect.width / 2 - 150,
                                        window.innerWidth - 320
                                    )
                                ),
                        transform:
                            step.position === "center" ? "translate(-50%, -50%)" : "none",
                        zIndex: 1200,
                    }}
                >
                    <div className="psl-tutorial-content">
                        <div className="psl-tutorial-header">
                            <div className="psl-tutorial-stepnum">{currentStep + 1}</div>
                            <h3>{step.title}</h3>
                        </div>
                        <p>{step.description}</p>

                        <div className="psl-tutorial-controls">
                            <button
                                className="psl-btn"
                                onClick={skipTutorial}
                                disabled={lockSkip}
                                title={
                                    lockSkip
                                        ? 'Click “New Campaign” first to continue or skip.'
                                        : undefined
                                }
                            >
                                Skip
                            </button>
                            <button
                                className="psl-btn"
                                onClick={prevStep}
                                disabled={currentStep === 0}
                            >
                                Previous
                            </button>
                            <button
                                className="psl-btn primary"
                                onClick={nextStep}
                                disabled={lockNext}
                                title={
                                    lockNext && step.requireModalOpenForProgress && !modalOpen
                                        ? 'Click “New Campaign” to open the dialog before going on.'
                                        : lockNext && step.lockNextWhileHeroVisible && heroVisible
                                            ? 'Connect your mailbox or click “Skip for now” in the card first.'
                                            : undefined
                                }
                            >
                                {currentStep === steps.length - 1 ? "Got it!" : "Next"}
                            </button>
                        </div>
                    </div>

                    {step.position !== "center" && (
                        <div
                            className="psl-tutorial-arrow"
                            style={{
                                borderTopColor: step.position === "top" ? "#fff" : "transparent",
                                borderBottomColor:
                                    step.position === "bottom" ? "#fff" : "transparent",
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default CampaignTutorial;